import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Clock3,
  Columns2,
  ExternalLink,
  Image,
  ListOrdered,
  MessageCircleQuestion,
  UserRound,
} from "lucide-react";
import React, { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { Process } from "../../api/types";
import { formatDuration } from "../../utils";
import type { ScreenshotRef } from "../screenshots";
import ProcessCaseFindings from "./ProcessCaseFindings";
import type { CaseNoteContext } from "./ProcessCaseNoteBadges";
import ProcessInstancePicker, {
  type ProcessInstanceOption,
  useProcessSourceIdentity,
} from "./ProcessInstancePicker";
import ProcessOccurrenceEvidence from "./ProcessOccurrenceEvidence";
import type { TransitionWaitAskContext } from "./ProcessOverviewPanel";
import { appIconUri } from "./appIcon";
import { type CaseReading, notableWaitThreshold } from "./processCaseNotes";
import { type ProcessInstance, type StepNamesById, stepWaitSeconds } from "./processGraphModel";
import { recordingAnalysisPath } from "./processInstanceIdentity";
import { screenshotRefsForInstance, screenshotRefsForSteps } from "./processScreenshots";
import { StepAppIcon, panelScrollBehavior, useOverflowClamp } from "./processSidebarUi";
import { useStepWheelKeys } from "./processStepWheel";
import { runTitle } from "./processInstanceLabels";

export type CaseStepAskContext = { id: string; name: string; stepIndex: number };

type ProcessInstanceTimelineProps = {
  process: Process;
  instance: ProcessInstance;
  reading?: { reading: CaseReading; context: CaseNoteContext };
  selectedStepIndex: number;
  scrollContainerRef: RefObject<HTMLDivElement>;
  nameById: StepNamesById;
  onSelectStep: (nodeId: string, stepIndex: number) => void;
  onShowScreenshots: (screenshots: ScreenshotRef[]) => void;
  onAskAboutInstance: (instance: { id: string; name: string }) => void;
  onAskAboutStep: (step: CaseStepAskContext) => void;
  onAskWaitingTime: (transition: TransitionWaitAskContext, stepIndex: number) => void;
  compareOptions: ProcessInstanceOption[];
  onCompareWith: (instanceId: string) => void;
  instanceOptions: ProcessInstanceOption[];
  onSelectInstance: (instanceId: string) => void;
  onReachBoundary: (kind: "start" | "end") => void;
};

const actionClass =
  "inline-flex min-h-8 items-center justify-center gap-1.5 rounded-md border border-outline bg-surface px-2.5 text-xs font-medium text-text-mid transition hover:bg-surface-strong hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

const ProcessInstanceTimeline = ({
  process,
  instance,
  reading,
  selectedStepIndex,
  scrollContainerRef,
  nameById,
  onSelectStep,
  onShowScreenshots,
  onAskAboutInstance,
  onAskAboutStep,
  onAskWaitingTime,
  compareOptions,
  onCompareWith,
  instanceOptions,
  onSelectInstance,
  onReachBoundary,
}: ProcessInstanceTimelineProps) => {
  const { t } = useTranslation();
  const nodeById = useMemo(() => new Map(process.nodes.map(node => [node.id, node])), [process]);
  const identityFor = useProcessSourceIdentity(process);
  const identity = identityFor(instance.source);
  const instanceTitle = runTitle(instance, identity.instanceLabel);
  const instanceScreenshots = useMemo(() => screenshotRefsForInstance(instance), [instance]);
  const waitAfter = useMemo(() => stepWaitSeconds(instance.steps), [instance.steps]);
  const notableGapSeconds = notableWaitThreshold(reading?.context.typicalCaseSeconds ?? 0);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const descriptionRef = useRef<HTMLHeadingElement>(null);
  const descriptionOverflows = useOverflowClamp(descriptionRef, descriptionExpanded, instanceTitle);
  const contextRowRef = useRef<HTMLLIElement>(null);
  const timelineHeaderRef = useRef<HTMLDivElement>(null);
  const previousSelection = useRef<{ instanceId: string; index: number } | null>(null);
  const totalSteps = instance.steps.length;
  const focusIndex = Math.min(Math.max(selectedStepIndex, 0), Math.max(totalSteps - 1, 0));

  // Leave two preceding steps under the sticky header. Scroll this panel directly: scrollIntoView
  // also moves overflow-hidden ancestors, shifting the graph and exposing blank page space.
  useEffect(() => {
    const previous = previousSelection.current;
    if (
      (previous?.instanceId !== instance.id || previous.index !== focusIndex) &&
      (previous?.instanceId === instance.id || focusIndex > 0)
    ) {
      const scroller = scrollContainerRef.current;
      const contextRow = contextRowRef.current;
      const header = timelineHeaderRef.current;
      if (scroller && contextRow && header) {
        const top =
          scroller.scrollTop +
          contextRow.getBoundingClientRect().top -
          scroller.getBoundingClientRect().top -
          header.offsetHeight -
          8;
        // The final steps stay at the natural bottom; never add space to force their alignment.
        scroller.scrollTo({
          top: Math.max(0, Math.min(top, scroller.scrollHeight - scroller.clientHeight)),
          behavior: previous?.instanceId === instance.id ? panelScrollBehavior() : "auto",
        });
      }
    }
    previousSelection.current = { instanceId: instance.id, index: focusIndex };
  }, [instance.id, focusIndex, scrollContainerRef]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: each case starts with a compact description.
  useEffect(() => setDescriptionExpanded(false), [instance.id]);

  const goToStep = useCallback(
    (index: number) => {
      const step = instance.steps[index];
      if (step) onSelectStep(step.nodeId, index);
      else if (totalSteps > 0) onReachBoundary(index < 0 ? "start" : "end");
    },
    [instance.steps, onSelectStep, onReachBoundary, totalSteps],
  );
  useStepWheelKeys({
    enabled: totalSteps > 0,
    onMove: useCallback((delta: number) => goToStep(focusIndex + delta), [focusIndex, goToStep]),
  });

  return (
    <div className="mx-auto w-full max-w-5xl pb-4">
      <div className="space-y-3 px-4 py-4">
        <div>
          <h3
            ref={descriptionRef}
            className={`break-words text-base font-semibold leading-6 text-text ${descriptionExpanded ? "" : "line-clamp-2"}`}
          >
            {instanceTitle}
          </h3>
          {(descriptionOverflows || descriptionExpanded) && (
            <button
              type="button"
              aria-expanded={descriptionExpanded}
              onClick={() => setDescriptionExpanded(value => !value)}
              className="mt-1 rounded text-xs font-medium text-info focus-visible:ring-2 focus-visible:ring-info/30"
            >
              {t(descriptionExpanded ? "show_less" : "show_more")}
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-strong text-text-dim">
              <UserRound className="h-4 w-4" aria-hidden="true" />
            </span>
            <div className="min-w-0 text-xs leading-5">
              {instance.source.userName && (
                <p className="break-words font-medium text-text">{instance.source.userName}</p>
              )}
              <time className="text-text-dim" dateTime={instance.source.traceStart}>
                {identity.startTime}
              </time>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {instanceOptions.length > 1 && (
              <ProcessInstancePicker
                process={process}
                options={instanceOptions}
                selectedInstanceId={instance.id}
                onSelect={onSelectInstance}
                align="start"
                label={t("instances_in_scope")}
              >
                <button type="button" className={actionClass}>
                  {t("switch_instance")}
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </ProcessInstancePicker>
            )}
            {compareOptions.length > 0 && (
              <ProcessInstancePicker
                process={process}
                options={compareOptions}
                selectedInstanceId={null}
                onSelect={onCompareWith}
                align="start"
                label={t("compare_with")}
              >
                <button type="button" aria-label={t("compare_with")} className={actionClass}>
                  <Columns2 className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("compare")}
                </button>
              </ProcessInstancePicker>
            )}
            <button
              type="button"
              onClick={() => onAskAboutInstance({ id: instance.id, name: instanceTitle })}
              className={actionClass}
            >
              <MessageCircleQuestion className="h-3.5 w-3.5" aria-hidden="true" />
              {t("ask_about_instance")}
            </button>
            <Link
              to={recordingAnalysisPath(
                instance.source.recordingId,
                process.slug,
                identity.instanceNumber,
              )}
              state={{ tab: "overview" }}
              aria-label={t("open_instance_recording", { instance: instanceTitle })}
              className={`pd-button-link ${actionClass}`}
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              {t("processPanel.caseSession")}
            </Link>
          </div>
        </div>
        <dl className="flex divide-x divide-outline rounded-lg border border-outline bg-surface px-1 py-3">
          <div className="min-w-0 flex-1 px-3">
            <dt className="flex items-center gap-1.5 text-xs text-text-dim">
              <Clock3 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t("processPanel.caseActiveTime")}
            </dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums leading-6">
              {formatDuration(instance.source.avgDuration)}
            </dd>
          </div>
          <div className="min-w-0 flex-1 px-3">
            <dt className="flex items-center gap-1.5 text-xs text-text-dim">
              <ListOrdered className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t("processPanel.tab_steps")}
            </dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums leading-6">{totalSteps}</dd>
          </div>
          {instanceScreenshots.length > 0 && (
            <div className="min-w-0 flex-1 px-3">
              <dt className="flex items-center gap-1.5 text-xs text-text-dim">
                <Image className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {t("processPanel.caseEvidence")}
              </dt>
              <dd>
                <button
                  type="button"
                  onClick={() => onShowScreenshots(instanceScreenshots)}
                  className="mt-1 min-h-6 rounded text-left text-xs font-medium leading-6 text-info underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-info/30"
                >
                  {t("processPanel.caseScreenshots", { count: instanceScreenshots.length })}
                </button>
              </dd>
            </div>
          )}
        </dl>
        {reading && (
          <ProcessCaseFindings
            key={instance.id}
            instance={instance}
            reading={reading.reading}
            context={reading.context}
            nameById={nameById}
            onSelectStep={goToStep}
          />
        )}
      </div>

      <section aria-label={t("instance_steps_chronological")}>
        <div
          ref={timelineHeaderRef}
          className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 border-y border-outline bg-surface px-4 py-2"
        >
          <h3 className="text-sm font-semibold">{t("processPanel.caseTimeline")}</h3>
          {totalSteps > 0 && (
            <div className="flex items-center gap-2">
              <span aria-live="polite" className="text-xs tabular-nums text-text-mid">
                {t("processPanel.caseStepPosition", { current: focusIndex + 1, total: totalSteps })}
              </span>
              <button
                type="button"
                aria-label={t("processPanel.casePreviousStep")}
                disabled={focusIndex === 0}
                onClick={() => goToStep(focusIndex - 1)}
                className={`${actionClass} px-1.5 disabled:opacity-40`}
              >
                <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={t("processPanel.caseNextStep")}
                disabled={focusIndex === totalSteps - 1}
                onClick={() => goToStep(focusIndex + 1)}
                className={`${actionClass} px-1.5 disabled:opacity-40`}
              >
                <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
        <ol className="mx-4 mt-3 overflow-hidden rounded-lg border border-outline">
          {instance.steps.map((step, index) => {
            const node = nodeById.get(step.nodeId);
            const name = node?.name ?? step.nodeId;
            const selected = index === focusIndex;
            const screenshots = screenshotRefsForSteps(
              instance.source.recordingId,
              instance.source.userId,
              [step],
            );
            const gap = index > 0 ? waitAfter[index - 1] : null;
            const showGap = gap != null && gap >= notableGapSeconds;
            const previous = instance.steps[index - 1];
            const from = previous ? nodeById.get(previous.nodeId) : undefined;
            const occurrence = instance.steps
              .slice(0, index + 1)
              .filter(candidate => candidate.nodeId === step.nodeId).length;
            const occurrences = instance.steps.filter(
              candidate => candidate.nodeId === step.nodeId,
            ).length;
            return (
              <li
                ref={index === Math.max(0, focusIndex - 2) ? contextRowRef : undefined}
                key={`${instance.id}:${index}`}
                className={showGap ? "" : "border-t border-outline first:border-t-0"}
              >
                {showGap && previous && (
                  <div className="flex items-center gap-2 px-3 py-1.5 text-xs">
                    <span
                      className="flex-1 border-t border-dashed border-warning/30"
                      aria-hidden="true"
                    />
                    <button
                      type="button"
                      aria-label={t("ask_why_waiting_time_gap", {
                        duration: formatDuration(gap),
                        from: from?.name ?? previous.nodeId,
                        to: name,
                      })}
                      onClick={() =>
                        onAskWaitingTime(
                          {
                            from: {
                              id: previous.nodeId,
                              name: from?.name ?? previous.nodeId,
                              iconUri: appIconUri(from?.app ?? null),
                            },
                            to: {
                              id: step.nodeId,
                              name,
                              iconUri: appIconUri(node?.app ?? null),
                            },
                            averageWaitSeconds: gap,
                            transitionsCount: 1,
                          },
                          index,
                        )
                      }
                      className="inline-flex min-h-7 shrink-0 items-center gap-1.5 rounded-full border border-warning/20 bg-attention-soft px-2.5 text-xs font-medium tabular-nums text-warning-strong transition hover:bg-warning/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning/40"
                    >
                      <Clock3 className="h-3.5 w-3.5" aria-hidden="true" />
                      {t("processPanel.caseGap", {
                        duration: formatDuration(gap),
                      })}
                      <MessageCircleQuestion className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                    <span
                      className="flex-1 border-t border-dashed border-warning/30"
                      aria-hidden="true"
                    />
                  </div>
                )}
                <button
                  type="button"
                  aria-current={selected ? "step" : undefined}
                  aria-expanded={selected}
                  onClick={() => goToStep(index)}
                  className={`flex min-h-11 w-full scroll-mt-16 items-center gap-2 px-3 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40 ${selected ? "bg-focus-band" : "hover:bg-surface-strong"}`}
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface-strong text-xs tabular-nums text-text-mid">
                    {index + 1}
                  </span>
                  <span className="shrink-0" title={node?.app ?? undefined}>
                    <StepAppIcon app={node?.app} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      title={name}
                      className={`block text-sm font-medium text-text ${selected ? "break-words" : "truncate"}`}
                    >
                      {name}
                      {node?.app && <span className="sr-only">{node.app}</span>}
                    </span>
                    {!selected && step.description.trim() && (
                      <span className="mt-0.5 block truncate text-xs font-normal text-text-dim">
                        {step.description}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-xs tabular-nums text-text-mid">
                    {formatDuration(step.duration)}
                  </span>
                  {screenshots.length > 0 && (
                    <span
                      aria-hidden="true"
                      className="flex w-11 shrink-0 items-center justify-end gap-1 text-xs tabular-nums text-text-dim"
                    >
                      <Image className="h-3.5 w-3.5" />
                      {screenshots.length}
                    </span>
                  )}
                </button>
                {selected && (
                  <div className="bg-focus-band/30 px-3 py-3">
                    <ProcessOccurrenceEvidence
                      key={`${instance.id}:${index}`}
                      metadata={
                        occurrences > 1
                          ? t("processPanel.caseOccurrence", {
                              current: occurrence,
                              total: occurrences,
                            })
                          : undefined
                      }
                      description={step.description}
                      objects={step.objects}
                      screenshots={screenshots}
                      onShowScreenshots={onShowScreenshots}
                      onAsk={() =>
                        onAskAboutStep({
                          id: step.nodeId,
                          name,
                          stepIndex: index,
                        })
                      }
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </section>
    </div>
  );
};

export default ProcessInstanceTimeline;
